import { AlertCircle, RefreshCw } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { useAppShellBreadcrumb } from "@/shared/components/layout/useAppShellBreadcrumb";
import useTranslations from "@/shared/lang/useTranslations";

import { ClientAccountsModal } from "../components/ClientAccountsModal";
import { CreateSubclientModal } from "../components/CreateSubclientModal";
import { DeleteOrganizationSection } from "../components/DeleteOrganizationSection";
import { EditOrganizationModal } from "../components/EditOrganizationModal";
import { KycVerificationSection } from "../components/kyc/KycVerificationSection";
import { OrganizationClientsView } from "../components/OrganizationClientsView";
import { OrganizationContextHeader } from "../components/OrganizationContextHeader";
import { useOrganizationKycProfile } from "../hooks/useOrganizationKycProfile";
import { useOrganizationSummary } from "../hooks/useOrganizationSummary";
import {
  canDeleteOrganizations,
  canEditOrganization,
  canReadClientAccounts,
} from "../utils/clientsPermissions";

/**
 * `/app/clients/organizations/:organizationId` — level 2 of the internal master-detail, and the
 * organization's detail surface.
 *
 * Replaces the page content (no modal): context header, the organization's own KYC section, its
 * server-paginated clients table, and the danger zone that deletes it. The URL is shareable per organization.
 * The organization-scoped pieces (KYC, deletion) used to live in the client detail drawer's organization
 * branch — which the master-detail stopped opening, leaving them unreachable.
 */
const OrganizationClientsPage = () => {
  const { t } = useTranslations("clients");
  const { t: tGlobal } = useTranslations("global");
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  const { user, isInternalUser } = useAuth();

  const { organization, isLoading, error, refetch } = useOrganizationSummary(organizationId);
  // Corporate KYC lives here now: the organization is the subject of this page. Only INTERNAL staff manage it,
  // so members reaching this URL directly do not pay for the two extra requests.
  const { profile: organizationProfile, isLoading: isKycLoading } = useOrganizationKycProfile(
    isInternalUser ? organizationId : undefined,
    isInternalUser ? organization : null,
  );

  const breadcrumb = useMemo(
    () => [
      { label: tGlobal("sidebar_section_gestion") },
      { label: tGlobal("sidebar_menu_clients"), to: "/app/clients" },
      { label: organization?.name ?? "…" },
    ],
    [tGlobal, organization?.name],
  );
  useAppShellBreadcrumb(breadcrumb);

  const [editOrganizationOpen, setEditOrganizationOpen] = useState(false);
  const [accountsOpen, setAccountsOpen] = useState(false);
  const [createClientOpen, setCreateClientOpen] = useState(false);
  const [refreshToken, setRefreshToken] = useState(0);

  const handleClientsChanged = useCallback(() => {
    setRefreshToken((token) => token + 1);
    refetch();
  }, [refetch]);

  const goBack = useCallback(() => navigate("/app/clients"), [navigate]);

  // The route-level `RequireAccess to="/app/clients"` already enforces `clients:read`. A second check
  // here could only disagree with it, and the one that used to live here ended in `return true`.

  return (
    <div className="space-y-6 p-2">
      <OrganizationContextHeader
        organization={organization}
        isLoading={isLoading}
        onBack={goBack}
        onEditOrganization={canEditOrganization(user) ? () => setEditOrganizationOpen(true) : undefined}
        onViewAccounts={canReadClientAccounts(user) ? () => setAccountsOpen(true) : undefined}
        onCreateClient={() => setCreateClientOpen(true)}
      />

      {error && (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={
            <span className="flex flex-wrap items-center gap-3">
              {error}
              <ColocaButton variant="default" size="xs" icon={RefreshCw} onClick={refetch}>
                {t("error_retry")}
              </ColocaButton>
            </span>
          }
        />
      )}

      {isInternalUser &&
        (organizationProfile ? (
          <KycVerificationSection client={organizationProfile} canSendEmail sectionId="organization-kyc" />
        ) : (
          isKycLoading && <ColocaSkeleton className="h-40 w-full rounded-xl" />
        ))}

      <OrganizationClientsView
        organizationId={organizationId}
        refreshToken={refreshToken}
        onCreateClient={() => setCreateClientOpen(true)}
        onDataChanged={refetch}
      />

      {isInternalUser && organizationProfile && canDeleteOrganizations(user) && (
        <DeleteOrganizationSection organization={organizationProfile} onDeleted={goBack} />
      )}

      <EditOrganizationModal
        isOpen={editOrganizationOpen}
        onClose={() => setEditOrganizationOpen(false)}
        organizationId={editOrganizationOpen ? (organizationId ?? null) : null}
        onSuccess={handleClientsChanged}
      />

      {organizationId && (
        <ClientAccountsModal
          isOpen={accountsOpen}
          onClose={() => setAccountsOpen(false)}
          entityId={accountsOpen ? organizationId : null}
          entityDisplayName={organization?.name ?? ""}
          scope="organization"
        />
      )}

      <CreateSubclientModal
        isOpen={createClientOpen}
        onClose={() => setCreateClientOpen(false)}
        onSuccess={handleClientsChanged}
        defaultOrganizationId={organizationId}
        defaultOrganizationName={organization?.name}
      />
    </div>
  );
};

export { OrganizationClientsPage };
export default OrganizationClientsPage;
